import React, { useState,useEffect } from "react";

import {
  FaBoxOpen,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaTimes,
} from "react-icons/fa";

import { updateOrderStatus,getUserReturnRequestsApi } from "../services/api";
import html2pdf from "html2pdf.js";

import PrintInvoice from "./PrintInvoice";
import ReturnRequestDetails from "./ReturnRequestDetails";

export default function OrderDetails({ order, onClose }) {
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(
    order.order_status || "pending",
  );

  const [returnRequest, setReturnRequest] = useState(null);
  const [loadingReturnRequest, setLoadingReturnRequest] = useState(false);

  

  useEffect(() => {
    const fetchReturnRequest = async () => {
      if (!order?.userId || !order?._id) return;

      try {
        setLoadingReturnRequest(true);

        const response = await getUserReturnRequestsApi(order.userId);

        const requests = response?.returnRequests || response?.data || [];

        const matchingRequest = requests.find(
          (request) =>
            request.orderId?.toString() === order._id?.toString()
        );

        setReturnRequest(matchingRequest || null);
      } catch (error) {
        console.error("Failed to fetch return request:", error);
        setReturnRequest(null);
      } finally {
        setLoadingReturnRequest(false);
      }
    };

    fetchReturnRequest();
  }, [order]);

  if (!order) return null;

  // =====================================================

  // DATE FORMAT

  // =====================================================

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",

      month: "short",

      year: "numeric",
    });
  };

  // =====================================================

  // STATUS

  // =====================================================

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
      return "bg-green-100 text-green-700";

    case "shipped":
      return "bg-blue-100 text-blue-700";

    case "processing":
    case "pending":
      return "bg-yellow-100 text-yellow-700";

    case "returned":
      return "bg-purple-100 text-purple-700";

    case "cancelled":
      return "bg-red-100 text-red-700";

    default:
      return "bg-gray-100 text-gray-700";
    }
  };

  const shipping = order.shipping || {};

  const formatAmount = (amount) => Number(amount || 0).toLocaleString("en-IN");

  // =====================================================

  // PRINT

  // =====================================================

  const handleDownloadPDF = async () => {
    const invoiceElement = document.getElementById("bill-of-supply");

    if (!invoiceElement) {
      console.error("Invoice element not found");
      return;
    }

    const invoiceNumber = order.invoiceNo || order.invoiceNumber || order._id;

    const options = {
      margin: 0,
      filename: `Legacy-Decor-Invoice-${invoiceNumber}.pdf`,

      image: {
        type: "jpeg",
        quality: 0.98,
      },

      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      },

      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },

      pagebreak: {
        mode: ["avoid-all", "css", "legacy"],
      },
    };

    try {
      await html2pdf().set(options).from(invoiceElement).save();
    } catch (error) {
      console.error("PDF generation failed:", error);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;

    try {
      setUpdatingStatus(true);

      await updateOrderStatus(order._id, newStatus);

      setCurrentStatus(newStatus);
    } catch (error) {
      console.error("Failed to update order status:", error);

      // Revert dropdown if API fails
      setCurrentStatus(order.order_status || "pending");

      alert("Failed to update order status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 print-overlay">
      {/* =================================================

          MODAL

      ================================================= */}

      <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl overflow-hidden print-container">
        {/* =================================================

            HEADER

        ================================================= */}

        <div className="bg-[#FFF8EC] px-6 py-5 border-b border-gray-200 flex items-center justify-between print-header">
          <div>
            <p className="text-sm text-gray-500">Order Details</p>

            <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
              #{order._id}
            </h2>
          </div>

          {/* Don't print these buttons */}

          <div className="flex items-center gap-3 no-print">
            <button
              type="button"
              onClick={handleDownloadPDF}
              className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-3 py-1.5 rounded-md transition"
            >
              Download Bill
            </button>

            <button
              type="button"
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white hover:bg-gray-100 flex items-center justify-center transition"
              aria-label="Close"
            >
              <FaTimes className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* =================================================

            PRINTABLE ORDER

        ================================================= */}

        <div
          id="print-order"
          className="p-6 overflow-y-auto max-h-[calc(90vh-85px)]"
        >
          {/* CUSTOMER INFORMATION */}

          <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <FaUser className="text-[#E87932]" />
                Customer Information
              </h3>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* NAME */}

              <div className="flex gap-3">
                <FaUser className="text-[#E87932] mt-1" />

                <div>
                  <p className="text-xs text-gray-500">Full Name</p>

                  <p className="font-medium text-gray-800">
                    {shipping.name || "-"}
                  </p>
                </div>
              </div>

              {/* EMAIL */}

              <div className="flex gap-3">
                <FaEnvelope className="text-[#E87932] mt-1" />

                <div>
                  <p className="text-xs text-gray-500">Email</p>

                  <p className="font-medium text-gray-800 break-all">
                    {shipping.email || "-"}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* PHONE */}

              <div className="flex gap-3">
                <FaPhone className="text-[#E87932] mt-1" />

                <div>
                  <p className="text-xs text-gray-500">Phone</p>

                  <p className="font-medium text-gray-800">
                    {shipping.phone || "-"}
                  </p>
                </div>
              </div>

              {/* ADDRESS */}

              <div className="flex gap-3 ">
                <FaMapMarkerAlt className="text-[#E87932] mt-1" />

                <div>
                  <p className="text-xs text-gray-500">Delivery Address</p>

                  <p className="font-medium text-gray-800">
                    {shipping.address || "-"}

                    <br />

                    {shipping.city || "-"}

                    {shipping.state ? `, ${shipping.state}` : ""}

                    {shipping.pincode ? ` - ${shipping.pincode}` : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ORDER INFORMATION */}

          <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Order Information</h3>
            </div>

            <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-5">
              <div>
                <p className="text-xs text-gray-500">Order Date</p>

                <p className="font-medium text-gray-800 mt-1">
                  {formatDate(order.createdAt)}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Total Amount</p>

                <p className="font-semibold text-light_primary mt-1">
                  ₹{formatAmount(order.amount)}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Order Status</p>
                <div className="mt-1">
                  <select
                    value={currentStatus}
                    onChange={handleStatusChange}
                    disabled={updatingStatus}
                    className={`px-3 py-2 rounded-full text-xs font-medium border-0 outline-none cursor-pointer ${getStatusClass(
                      currentStatus,
                    )} ${
                      updatingStatus ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <option value="pending">Pending</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="returned">Returned</option>
                    <option value="cancelled">Cancelled</option>}
                  </select>

                  {updatingStatus && (
                    <span className="ml-2 text-xs text-gray-500">
                      Updating...
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* PAYMENT INFORMATION */}

          <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">
                Payment Information
              </h3>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <p className="text-xs text-gray-500">Payment Status</p>

                <p className="font-medium text-gray-800 mt-1 capitalize">
                  {order.status || "-"}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Payment ID</p>

                <p className="font-medium text-gray-800 mt-1 break-all">
                  {order.paymentId || "-"}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-500">Razorpay Order ID</p>

                <p className="font-medium text-gray-800 mt-1 break-all">
                  {order.razorpayOrderId || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* PRODUCTS */}

          <div className="border border-gray-200 rounded-xl overflow-hidden mb-6">
            <div className="bg-gray-50 px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Ordered Products</h3>
            </div>

            <div className="p-5 space-y-5">
              {order.items?.map((item, index) => (
                <div
                  key={`${item.productId}-${index}`}
                  className="flex gap-4 items-center pb-5 border-b border-gray-100 last:border-b-0 last:pb-0"
                >
                  <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-gray-200">
                    {item.image?.[0] ? (
                      <img
                        src={item.image[0]}
                        alt={item.title || "Product"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <FaBoxOpen className="text-gray-400" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1">
                    <h3 className="font-medium text-gray-800">{item.title}</h3>

                    <p className="text-sm text-gray-500 mt-1">
                      ₹{formatAmount(item.price)} × {item.quantity}
                    </p>
                  </div>

                  <div className="font-semibold text-gray-800">
                    ₹
                    {formatAmount(
                      Number(item.price || 0) * Number(item.quantity || 0),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
            
          <div className="">
            {loadingReturnRequest ? (
            <div className="border border-gray-200 rounded-xl p-5 mb-6">
              <p className="text-sm text-gray-500">
                Loading return request...
              </p>
            </div>
          ) : returnRequest ? (
            <ReturnRequestDetails
              returnRequest={returnRequest}
              onUpdated={(updatedReturnRequest) => {
                setReturnRequest(updatedReturnRequest);
              }}
            />
          ) : null}
          </div>
            
        </div>
      </div>

      {/* =====================================================
          HIDDEN INVOICE USED FOR PDF
      ===================================================== */}

      <div
        style={{
          position: "fixed",
          left: "-10000px",
          top: "0",
          width: "210mm",
          background: "#ffffff",
          zIndex: -1,
        }}
      >
        <div id="print-invoice">
          <PrintInvoice order={order} />
        </div>
      </div>
    </div>
  );
}
